import { UserInteractionServiceV1 } from '@volkswagen-onehub/user-interaction-service';
import {
	DatoEconomico,
	DetalleFinanciacionType,
	FinanciacionType,
	PeriodicidadType,
} from '../services/ofertas-service/models/ofertas';

//Obtenemos los 2 datos economicos con mas prioridad
export const getDatosEconomicos = (
	datosEconomicos: DatoEconomico[],
): DatoEconomico[] => {
	if (!Array.isArray(datosEconomicos) || datosEconomicos.length === 0) {
		return [];
	}

	return [...datosEconomicos]
		.sort((a, b) => b.prioridad - a.prioridad)
		.slice(0, 2);
};

//Obtenemos el pago unico con mas prioridad
export const getPagoUnico = (
	datosEconomicos: DatoEconomico[],
): DatoEconomico | null => {
	return (
		datosEconomicos
			.filter((dato) => dato.periodicidad === PeriodicidadType.PAGO_UNICO)
			.sort((a, b) => b.prioridad - a.prioridad)[0] || null
	);
};

//Obtenemos el pago financiado con mas prioridad
export const getPagoFinanciado = (
	datosEconomicos: DatoEconomico[],
): DatoEconomico | null => {
	return (
		datosEconomicos
			.filter((dato) => dato.periodicidad !== PeriodicidadType.PAGO_UNICO)
			.sort((a, b) => b.prioridad - a.prioridad)[0] || null
	);
};

export const getDetalleFinanciacion = (
	datosEconomicos: DatoEconomico,
	detalle: DetalleFinanciacionType,
): string => {
	switch (detalle) {
		case DetalleFinanciacionType.ENTRADA:
			return (
				formatPrecio(datosEconomicos.pagoEntrada || 0) +
				'€ de entrada' +
				(datosEconomicos.ivaIncluido ? ' (IVA incluido)' : ' (sin IVA)')
			);
		case DetalleFinanciacionType.CUOTAS:
			return datosEconomicos.plazos + '\u00A0cuotas\u00A0';
		case DetalleFinanciacionType.CUOTA_FINAL:
			return (
				'Cuota final: ' + formatPrecio(datosEconomicos.pagoFinal || 0) + '€'
			);
		case DetalleFinanciacionType.TAE:
			return datosEconomicos.tae + '%\u00A0';
		default:
			return '';
	}
};

export const capitalizeSentence = (string: string): string => {
	if (!string) return '';
	return string.charAt(0).toUpperCase() + string.slice(1);
};

export const formatPrecio = (precio: number): string => {
	return Number.isInteger(precio)
		? precio.toLocaleString('es-ES', { useGrouping: true })
		: precio.toLocaleString('es-ES', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
				useGrouping: true,
			});
};

export const getEtiquetaDgt = (etiqueta: string) => {
	const etiquetaC =
		'https://cdn.volkswagen.es/repository/app/vw-match/assets/icon/stock_etiqueta_c.png';
	const etiquetaEco =
		'https://cdn.volkswagen.es/repository/app/vw-match/assets/icon/stock_etiqueta_eco.png';
	const etiquetaZero =
		'https://cdn.volkswagen.es/repository/app/vw-match/assets/icon/stock_etiqueta_0.png';

	let imageEtiqueta: string;

	switch (etiqueta) {
		case 'C':
			imageEtiqueta = etiquetaC;
			break;
		case '0':
			imageEtiqueta = etiquetaZero;
			break;
		case 'ECO':
			imageEtiqueta = etiquetaEco;
			break;
		default:
			imageEtiqueta = etiquetaC;
			break;
	}

	return imageEtiqueta;
};

export const getFinanciacionType = (datoEconomico: DatoEconomico): string => {
	switch (datoEconomico.tipoFinanciacion) {
		case FinanciacionType.MY_WAY:
			return 'My Way';
		case FinanciacionType.MY_WAY_SERVICE:
			return 'My Way';
		case FinanciacionType.RENTING:
			return 'Renting';
		case FinanciacionType.MY_RENTING:
			return 'My Renting';
		case FinanciacionType.FLEX_RENTING:
			return 'Flex Renting';
		case FinanciacionType.LEASING:
			return 'Leasing';

		default:
			return capitalizeSentence(
				(datoEconomico.tipoFinanciacion as string).replace(/-/g, ' '),
			);
	}
};

export const getFinanciacionRedirection = (
	datoEconomico: DatoEconomico,
	brand?: string,
): string | undefined => {
	switch (datoEconomico.tipoFinanciacion) {
		case FinanciacionType.MY_WAY:
			return brand === 'N'
				? 'https://www.volkswagen-comerciales.es/es/financiacion-seguros/my-way.html'
				: 'https://www.volkswagen.es/es/financiacion-renting-seguros/my-way.html';
		case FinanciacionType.MY_WAY_SERVICE:
			return undefined;
		case FinanciacionType.RENTING:
			return undefined;
		case FinanciacionType.MY_RENTING:
			return brand === 'N'
				? 'https://www.volkswagen-comerciales.es/es/financiacion-seguros/my-renting.html'
				: 'https://www.volkswagen.es/es/financiacion-renting-seguros/my-renting.html';
		case FinanciacionType.FLEX_RENTING:
			return undefined;
		default:
			return undefined;
	}
};

export const getFinanciacionPeriodicidad = (
	datosEconomicos: DatoEconomico[],
): string | undefined => {
	const datoEconomico =
		datosEconomicos
			.filter((dato) => dato.periodicidad != PeriodicidadType.PAGO_UNICO)
			.sort((a, b) => b.prioridad - a.prioridad)[0] || null;

	switch (datoEconomico.periodicidad) {
		case PeriodicidadType.MENSUAL:
			return '€/mes';
		case PeriodicidadType.ANUAL:
			return '€/año';
		case PeriodicidadType.DIARIO:
			return '€/dia';
		default:
			return '€/mes';
	}
};

export const getMiddleModalidad = (
	pagoUnico: DatoEconomico,
	pagoFinanciado: DatoEconomico | null,
): string => {
	if (
		pagoFinanciado &&
		pagoFinanciado.modalidad &&
		pagoUnico.modalidad !== pagoFinanciado.modalidad &&
		pagoUnico.precio
	) {
		return pagoFinanciado.modalidad + '\u00A0';
	}
	return 'por \u00A0';
};

export function transformToSlashes(input: string): string {
	const index = input.search(/[^a-zA-Z0-9]/);
	if (index === -1) return input;

	const prefix = input.slice(0, index);
	const segments = input.slice(index + 1).split(/[^a-zA-Z0-9]+/);

	return prefix + '/' + segments.map((s) => s.slice(-3)).join('/');
}

export function extractCodeByCategory(
	options: { category: string; code: string }[],
	category: string,
): string {
	const option = options.find((index) => index.category === category);
	return option ? option.code.split(':')[1]?.trim() || '' : '';
}

export function extractUnderscore(code: string): string {
	return code.includes('_') ? code.split('_')[1]?.trim() || '' : '';
}

export function getCO2Value(
	emissions: {
		phase: string;
		value_type: string;
		energy_management_type?: string;
		value: number;
	}[],
	combustible: string,
	webcalc: boolean,
): number {
	let finalValue = 0;
	const isHybrid = combustible == 'Híbrido enchufable' ? true : false;
	const emissionValue =
		emissions.find(
			(wltp) =>
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CO2' &&
				(!isHybrid || wltp.energy_management_type === 'WEIGHTED'),
		)?.value ?? null;

	if (emissionValue !== null) {
		if (webcalc) {
			finalValue = Math.ceil(emissionValue);
		} else {
			finalValue = parseFloat(emissionValue.toFixed(1));
		}
	}

	return finalValue;
}

export function getConsumptionText(
	interpolations: any[],
	combustible: string,
): string {
	const isHybrid = combustible == 'Híbrido enchufable' ? true : false;
	const isMildHybrid =
		combustible == 'Gasolina mild Hybrid (etiqueta ECO)' ? true : false;
	const isElectric = combustible == 'Eléctrico' ? true : false;
	let consumptionElectrical = null;
	let emissionsElectrical = null;
	let consumptionPetrol = null;
	let emissionsPetrol = null;

	if (isHybrid) {
		consumptionElectrical = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'WEIGHTED' &&
				wltp.fuel_type === 'ELECTRICAL' &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CONSUMPTION',
		);
		emissionsElectrical = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'WEIGHTED' &&
				wltp.fuel_type === 'ELECTRICAL' &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CO2',
		);

		consumptionPetrol = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'WEIGHTED' &&
				wltp.fuel_type === 'PETROL' &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CONSUMPTION',
		);
		emissionsPetrol = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'WEIGHTED' &&
				wltp.fuel_type === 'PETROL' &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CO2',
		);
	} else if (isElectric) {
		consumptionElectrical = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'PURE' &&
				wltp.fuel_type === 'ELECTRICAL' &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CONSUMPTION',
		);
		emissionsElectrical = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'PURE' &&
				wltp.fuel_type === 'ELECTRICAL' &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CO2',
		);
	} else if (isMildHybrid) {
		consumptionPetrol = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'SUSTAINING' &&
				(wltp.fuel_type === 'PETROL' || wltp.fuel_type === 'DIESEL') &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CONSUMPTION',
		);
		emissionsPetrol = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'SUSTAINING' &&
				(wltp.fuel_type === 'PETROL' || wltp.fuel_type === 'DIESEL') &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CO2',
		);
	} else {
		consumptionPetrol = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'PURE' &&
				(wltp.fuel_type === 'PETROL' || wltp.fuel_type === 'DIESEL') &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CONSUMPTION',
		);
		emissionsPetrol = interpolations.find(
			(wltp) =>
				wltp.energy_management_type === 'PURE' &&
				(wltp.fuel_type === 'PETROL' || wltp.fuel_type === 'DIESEL') &&
				wltp.phase === 'COMBINED' &&
				wltp.value_type === 'CO2',
		);
	}

	let text = '';
	if (consumptionElectrical) {
		text +=
			'Eléctrico - Consumo ' +
			Math.ceil(consumptionElectrical.value) +
			' ' +
			consumptionElectrical.unit +
			'¹';
		if (emissionsElectrical)
			text +=
				' y emisiones ' +
				Math.ceil(emissionsElectrical.value) +
				' ' +
				emissionsElectrical.unit +
				'¹';
	}

	if (consumptionElectrical && consumptionPetrol) text += ' | ';

	if (consumptionPetrol) {
		text +=
			'Gasolina - Consumo ' +
			Math.round(consumptionPetrol.value * 10) / 10 +
			' ' +
			consumptionPetrol.unit +
			'¹';
		if (emissionsPetrol)
			text +=
				' y emisiones ' +
				Math.round(emissionsPetrol.value) +
				' ' +
				emissionsPetrol.unit +
				'¹';
	}

	return text;
}

export function actionRedirect(
	userInteractionService: UserInteractionServiceV1,
	action: string,
): void {
	const [actionPath, paramString] = action.split('?');
	const handlerName = actionPath.replace('action:', '');
	const handler = userInteractionService.get(handlerName);

	const params: Record<string, string | string[]> = {};
	let paramsActionActivate = '';

	// Reset si no viene dealer en paramString → poner uno “neutral”
	if (!paramString || !paramString.includes('dealer=')) {
		params.dealer = 'none';
		paramsActionActivate = 'dealer=none';
	}

	if (paramString) {
		paramsActionActivate = paramString;
		const paramPairs = paramString.split('&');

		paramPairs.forEach((pair) => {
			const [key, value] = pair.split('=');
			params[key] = value;
		});
	}

	// @ts-expect-error - onActivate signature mismatch with handler type
	handler.onActivate(params, paramsActionActivate);
}
